import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { RootState, useAppDispatch } from "../../../redux/store";

import { FaseLegacy, saveFaseLegacyOperatore } from "./odpLegacySlice";
import { Operatore } from "../personale/operatoriSlice";

import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";

interface OperatoreSelectInlineProps {
    fase: FaseLegacy;
    odpId: number;
    consentiModifica: boolean;
    minWidth?: number | string;
}

const OperatoreSelectInline = ({
    fase,
    odpId,
    consentiModifica,
    minWidth = 160,
}: OperatoreSelectInlineProps) => {
    const dispatch = useAppDispatch();

    const operatori = useSelector<RootState, Operatore[]>(
        (state) => state.operatoriState?.operatoriList?.results || []
    );

    const [saving, setSaving] = useState(false);
    const [justSaved, setJustSaved] = useState(false);
    const justSavedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (justSavedTimer.current) clearTimeout(justSavedTimer.current);
        };
    }, []);

    // Operatori del reparto della fase + l'operatore corrente (se diverso, per non
    // far sparire un'assegnazione storica eventualmente incongruente).
    const operatoriDelReparto = useMemo(() => {
        if (!fase.reparto) return [];
        return operatori.filter(
            (o) =>
                o.attivo && (o.reparto === fase.reparto || o.id === fase.operatore)
        );
    }, [operatori, fase.reparto, fase.operatore]);

    const valoreCorrente: number | "" = fase.operatore ?? "";

    const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!fase.id) return;
        const raw = event.target.value;
        const operatoreId = raw === "" ? null : Number(raw);

        setSaving(true);
        const risultato = await dispatch(
            saveFaseLegacyOperatore({
                odp_id: odpId,
                fase_id: fase.id,
                operatore_id: operatoreId,
            })
        );
        setSaving(false);

        if (risultato.meta.requestStatus === "fulfilled") {
            setJustSaved(true);
            if (justSavedTimer.current) clearTimeout(justSavedTimer.current);
            justSavedTimer.current = setTimeout(() => setJustSaved(false), 1500);
        }
    };

    const disabled = !consentiModifica || !fase.reparto;
    const tooltip = !fase.reparto
        ? "Reparto non definito: assegnare prima un reparto"
        : fase.operatore_display ?? "Nessun operatore";

    return (
        <Tooltip title={tooltip}>
            <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth }}
                onClick={(e) => e.stopPropagation()}
            >
                <TextField
                    select
                    size="small"
                    value={valoreCorrente}
                    onChange={handleChange}
                    disabled={disabled}
                    fullWidth
                    slotProps={{ inputLabel: { shrink: true } }}
                >
                    <MenuItem value="">— nessuno —</MenuItem>
                    {operatoriDelReparto.map((op) => (
                        <MenuItem key={op.id} value={op.id}>
                            {op.nominativo || op.codice}
                        </MenuItem>
                    ))}
                </TextField>
                {saving && <CircularProgress size={14} />}
                {!saving && justSaved && (
                    <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />
                )}
            </Box>
        </Tooltip>
    );
};

export default OperatoreSelectInline;
